import { useState, useCallback } from 'react';
import {
  Bookmark,
  FolderPlus,
  Trash2,
  Pencil,
  Check,
  X,
  FileText,
  FolderOpen,
} from 'lucide-react';
import { useBookmarksContext } from '@/contexts/BookmarksContext';
import { useBookmarkFolders } from '@/hooks/useBookmarks';
import { DocTypeBadge } from '@/components/docs/DocTypeBadge';
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { docDeepLinkPath, withDocParam } from '@/lib/docDeepLink';
import {
  isLaunchHost,
  getTenantSlug,
  rememberedTenantSlug,
  launchUrl,
  captureAppUrl,
} from '@/utils/subdomain';

export default function BookmarksPage() {
  const { bookmarks, remove, update } = useBookmarksContext();
  const { folders, create: createFolder, update: updateFolder, remove: removeFolder } = useBookmarkFolders();
  const navigate = useNavigate();
  const { search: currentSearch } = useLocation();

  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [editingFolder, setEditingFolder] = useState<string | null>(null);
  const [editFolderName, setEditFolderName] = useState('');

  const filteredBookmarks = selectedFolder === 'unfiled'
    ? bookmarks.filter((b) => !b.folderId)
    : selectedFolder
      ? bookmarks.filter((b) => b.folderId === selectedFolder)
      : bookmarks;

  const handleOpenDoc = useCallback((docId: string) => {
    if (!isLaunchHost()) {
      const slug = getTenantSlug() ?? rememberedTenantSlug();
      window.location.href = slug
        ? launchUrl(slug, docDeepLinkPath(docId))
        : captureAppUrl(docDeepLinkPath(docId), null);
      return;
    }
    navigate({ pathname: '/docs', search: withDocParam(currentSearch, docId) });
  }, [navigate, currentSearch]);

  const handleCreateFolder = useCallback(async () => {
    const name = newFolderName.trim();
    if (!name) return;
    await createFolder(name);
    setNewFolderName('');
    setShowNewFolder(false);
  }, [newFolderName, createFolder]);

  const handleRenameFolder = useCallback(async (folderId: string) => {
    const name = editFolderName.trim();
    if (!name) return;
    await updateFolder(folderId, { name });
    setEditingFolder(null);
    setEditFolderName('');
  }, [editFolderName, updateFolder]);

  const handleDeleteFolder = useCallback(async (folderId: string) => {
    if (!window.confirm('Delete this folder? Bookmarks inside will become unfiled.')) return;
    await removeFolder(folderId);
    if (selectedFolder === folderId) setSelectedFolder(null);
  }, [removeFolder, selectedFolder]);

  const handleMoveBookmark = useCallback(async (docIdHash: string, folderId: string | null) => {
    await update(docIdHash, { folderId });
  }, [update]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-200 dark:border-slate-800 shrink-0">
        <Bookmark className="w-5 h-5 text-amber-500" />
        <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Bookmarks</h1>
        <span className="text-sm text-slate-400">{bookmarks.length}/10</span>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Folder sidebar */}
        <aside className="w-56 shrink-0 border-r border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 overflow-y-auto">
          <div className="p-3">
            <button
              onClick={() => setSelectedFolder(null)}
              className={cn(
                'flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm transition-colors',
                selectedFolder === null
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-medium'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800',
              )}
            >
              <Bookmark className="w-4 h-4 shrink-0" />
              All bookmarks
            </button>

            <button
              onClick={() => setSelectedFolder('unfiled')}
              className={cn(
                'flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm transition-colors',
                selectedFolder === 'unfiled'
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-medium'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800',
              )}
            >
              <FileText className="w-4 h-4 shrink-0" />
              Unfiled
            </button>

            <div className="mt-3 mb-1 px-3 flex items-center justify-between">
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Folders</span>
              <button
                onClick={() => setShowNewFolder(true)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                title="New folder"
              >
                <FolderPlus className="w-3.5 h-3.5" />
              </button>
            </div>

            {showNewFolder && (
              <div className="flex items-center gap-1 px-3 py-1">
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleCreateFolder(); if (e.key === 'Escape') setShowNewFolder(false); }}
                  placeholder="Folder name"
                  autoFocus
                  className="flex-1 min-w-0 bg-transparent text-sm text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded px-2 py-1 outline-none focus:border-blue-400"
                />
                <button onClick={handleCreateFolder} className="text-green-500 hover:text-green-600 shrink-0">
                  <Check className="w-4 h-4" />
                </button>
                <button onClick={() => setShowNewFolder(false)} className="text-slate-400 hover:text-slate-600 shrink-0">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {folders.map((folder) => (
              <div key={folder.folderId} className="group flex items-center gap-1">
                {editingFolder === folder.folderId ? (
                  <div className="flex items-center gap-1 flex-1 px-3 py-1">
                    <input
                      type="text"
                      value={editFolderName}
                      onChange={(e) => setEditFolderName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleRenameFolder(folder.folderId); if (e.key === 'Escape') setEditingFolder(null); }}
                      autoFocus
                      className="flex-1 min-w-0 bg-transparent text-sm text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded px-2 py-1 outline-none focus:border-blue-400"
                    />
                    <button onClick={() => handleRenameFolder(folder.folderId)} className="text-green-500 hover:text-green-600 shrink-0">
                      <Check className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setEditingFolder(null)} className="text-slate-400 hover:text-slate-600 shrink-0">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => setSelectedFolder(folder.folderId)}
                      className={cn(
                        'flex items-center gap-2 flex-1 min-w-0 px-3 py-2 rounded-lg text-sm transition-colors',
                        selectedFolder === folder.folderId
                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-medium'
                          : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800',
                      )}
                    >
                      <FolderOpen className="w-4 h-4 shrink-0" />
                      <span className="truncate">{folder.name}</span>
                    </button>
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 pr-1 shrink-0 transition-opacity">
                      <button
                        onClick={() => { setEditingFolder(folder.folderId); setEditFolderName(folder.name); }}
                        className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                        title="Rename"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => handleDeleteFolder(folder.folderId)}
                        className="p-1 text-slate-400 hover:text-red-500"
                        title="Delete folder"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </aside>

        {/* Bookmarks list */}
        <div className="flex-1 overflow-y-auto">
          {filteredBookmarks.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-2 text-slate-400">
              <Bookmark className="w-8 h-8" />
              <p className="text-sm">
                {bookmarks.length === 0
                  ? 'No bookmarks yet. Bookmark documents from the reader or search results.'
                  : 'No bookmarks in this folder.'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-200 dark:divide-slate-800">
              {filteredBookmarks.map((bm) => (
                <div
                  key={bm.docIdHash}
                  className="flex items-start gap-3 px-6 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group"
                >
                  <button
                    onClick={() => handleOpenDoc(bm.docId)}
                    className="flex items-start gap-3 flex-1 min-w-0 text-left"
                  >
                    <FileText className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{bm.title}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{bm.repoFullName}/{bm.path}</p>
                      {bm.note && (
                        <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5 italic">{bm.note}</p>
                      )}
                    </div>
                    <DocTypeBadge type={bm.docType} />
                  </button>

                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                    {/* Move to folder */}
                    {folders.length > 0 && (
                      <select
                        value={bm.folderId || ''}
                        onChange={(e) => handleMoveBookmark(bm.docIdHash, e.target.value || null)}
                        className="text-[10px] bg-transparent border border-slate-200 dark:border-slate-700 rounded px-1 py-0.5 text-slate-500 dark:text-slate-400 outline-none"
                        title="Move to folder"
                      >
                        <option value="">Unfiled</option>
                        {folders.map((f) => (
                          <option key={f.folderId} value={f.folderId}>{f.name}</option>
                        ))}
                      </select>
                    )}
                    <button
                      onClick={() => remove(bm.docIdHash)}
                      className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                      title="Remove bookmark"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
